import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {
  type MilestoneShowResponse,
  MilestoneShowResponseSchema,
  MilestoneShowOwnerType,
} from '@github-ui/dotcom-schema/types/issues/v1/milestone_show'
import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {repoAppBuilder} from '@github-ui/repo-app-builder'
import {
  createRecordDocumentAdapter,
  type DocumentAdapterContext,
  type DocumentAdapterResult,
  type PendingConnectionOperation,
} from '@github-ui/optimistic-connection'

import {createIssuesIndexMainQuery, createIssuesIndexSecondaryQuery} from './issues-index-content-route'

export type MilestoneHeaderResponse = {
  repository: {
    id: string
    name: string
    nameWithOwner: string
    isArchived: boolean
    isDisabled: boolean
    isInOrganization: boolean
    isLocked: boolean
    viewerCanUpdateMilestones: boolean
    owner: {
      __typename: 'Organization' | 'User'
      id: string
      login: string
    }
    milestone: {
      id: string
      number: number
      title: string
      closed: boolean
      closedIssueCount: number
      openIssueCount: number
      description: string | null
      descriptionHTML: string | null
      dueOn: string | null
      progressPercentage: number
      updatedAt: string
      repository: {
        id: string
        nameWithOwner: string
      }
    }
  }
}

function invalidMilestoneResponse(): never {
  throw new Error('Invalid milestone response')
}

export function milestoneHeaderToView(response: MilestoneShowResponse): MilestoneHeaderResponse {
  const repository = response.repository
  const owner = repository?.owner
  const milestone = repository?.milestone
  const milestoneRepository = milestone?.repository
  if (!repository || !owner || !milestone || !milestoneRepository) invalidMilestoneResponse()

  const ownerType =
    owner.ownerType === MilestoneShowOwnerType.USER
      ? 'User'
      : owner.ownerType === MilestoneShowOwnerType.ORGANIZATION
        ? 'Organization'
        : invalidMilestoneResponse()

  return {
    repository: {
      id: repository.id,
      name: repository.name,
      nameWithOwner: repository.nameWithOwner,
      isArchived: repository.isArchived,
      isDisabled: repository.isDisabled,
      isInOrganization: repository.isInOrganization,
      isLocked: repository.isLocked,
      viewerCanUpdateMilestones: repository.viewerCanUpdateMilestones,
      owner: {id: owner.id, login: owner.login, __typename: ownerType},
      milestone: {
        id: milestone.id,
        number: milestone.number,
        title: milestone.title,
        closed: milestone.closed,
        closedIssueCount: milestone.closedIssueCount,
        openIssueCount: milestone.openIssueCount,
        description: milestone.description ?? null,
        descriptionHTML: milestone.descriptionHtml ?? null,
        dueOn: milestone.dueOn ?? null,
        progressPercentage: milestone.progressPercentage,
        updatedAt: milestone.updatedAt,
        repository: {id: milestoneRepository.id, nameWithOwner: milestoneRepository.nameWithOwner},
      },
    },
  }
}

export function milestoneIssuesQuery(number: number, closed: boolean, requestedQuery?: string | null) {
  if (requestedQuery) {
    return `(${requestedQuery}) AND milestone-number:${number} AND archived:false`
  }
  return `state:${closed ? 'closed' : 'open'} milestone-number:${number} archived:false sort:milestone_prio-desc`
}

export function milestoneHeaderQueryDeps(owner: string, repo: string, number: number) {
  return {pathname: `/${owner}/${repo}/milestone/${number}/page_data/header`}
}

export function milestoneIssuesQueryDeps(
  owner: string,
  repo: string,
  number: number,
  closed: boolean,
  page: number,
  requestedQuery?: string | null,
) {
  return {
    pathname: `/${owner}/${repo}/issues`,
    searchParams: {q: milestoneIssuesQuery(number, closed, requestedQuery), page: String(page)},
  }
}

export function milestoneIssuesSecondaryQueryDeps(
  owner: string,
  repo: string,
  number: number,
  closed: boolean,
  page: number,
  requestedQuery?: string | null,
) {
  return {
    pathname: `/${owner}/${repo}/issues/deferred`,
    searchParams: {q: milestoneIssuesQuery(number, closed, requestedQuery), page: String(page)},
  }
}

export function buildMilestoneShowRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  const mainQuery = createIssuesIndexMainQuery({payloadRouteId: 'issuesIndexContentRoute'})
  const secondaryQuery = createIssuesIndexSecondaryQuery()

  return appBuilder.createQueryRouteConfig('milestoneShowRoute', {
    path: '/:owner/:repo/milestone/:number',
    queries: [
      {
        queryName: 'milestoneRoute',
        queryDeps: ({params}) =>
          milestoneHeaderQueryDeps(params.owner, params.repo, Number.parseInt(params.number, 10)),
        schema: MilestoneShowResponseSchema,
        type: QueryRouteQueryType.Blocking,
      },
      // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The shared main query decodes with RepoIssuesDashboardContentResponseSchema.
      {
        ...mainQuery,
        queryDeps: ({params, searchParams}) =>
          milestoneIssuesQueryDeps(
            params.owner,
            params.repo,
            Number.parseInt(params.number, 10),
            searchParams.get('closed') === '1',
            1,
            searchParams.get('q'),
          ),
      },
      // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The shared secondary query decodes with RepoIssuesDashboardContentResponseSchema.
      {
        ...secondaryQuery,
        queryDeps: ({params, searchParams}) =>
          milestoneIssuesSecondaryQueryDeps(
            params.owner,
            params.repo,
            Number.parseInt(params.number, 10),
            searchParams.get('closed') === '1',
            1,
            searchParams.get('q'),
          ),
      },
    ],
  })
}

export const milestoneShowRoute = buildMilestoneShowRoute(repoAppBuilder)

export function milestoneHeaderQueryKey(owner: string, repo: string, number: number) {
  return milestoneShowRoute.queries.milestoneRoute.makeQueryKey(milestoneHeaderQueryDeps(owner, repo, number))
}

function milestoneFieldsToHeader(fields: Readonly<Record<string, unknown>>) {
  const {descriptionHTML, ...rest} = fields
  return {
    ...rest,
    ...('dueOn' in fields ? {dueOn: fields.dueOn ?? undefined} : {}),
    ...('description' in fields ? {description: fields.description ?? undefined} : {}),
    ...(descriptionHTML === undefined ? {} : {descriptionHtml: descriptionHTML ?? undefined}),
  }
}

const milestoneHeaderRecords = createRecordDocumentAdapter({mapFields: milestoneFieldsToHeader})

function isMilestoneHeader(document: unknown): document is MilestoneShowResponse {
  return (
    document !== null &&
    typeof document === 'object' &&
    '$typeName' in document &&
    document.$typeName === MilestoneShowResponseSchema.typeName
  )
}

export function milestoneHeaderDocumentAdapter<T>(
  document: T,
  change: PendingConnectionOperation,
  context: DocumentAdapterContext,
): DocumentAdapterResult<T> {
  if (!isMilestoneHeader(document)) return undefined
  const milestone = document.repository?.milestone
  if (!document.repository || !milestone) return undefined
  // Protobuf omits absent optional fields; these are still selected by the header query.
  return milestoneHeaderRecords(
    {
      ...document,
      repository: {
        ...document.repository,
        milestone: {
          ...milestone,
          dueOn: milestone.dueOn,
          description: milestone.description,
          descriptionHtml: milestone.descriptionHtml,
        },
      },
    },
    change,
    context,
  )
}
